import { useState, useCallback, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAssessment } from '../../../../../contexts/AssessmentContext'
import { calculateScores } from '../../../../../shared/utils/scoring'
import threatProfiles from '../../../../../shared/data/threatProfiles.json'
import regulatoryUpdates from '../../../../../shared/data/regulatoryUpdates.json'
import ThreatContextStep from '../../../../assessment/components/ThreatContextStep'
import ImpactCalibrationStep from '../../../../assessment/components/ImpactCalibrationStep'
import RegulatoryContextSection from '../../../../assessment/components/RegulatoryContextSection'
import FrameworkApplicabilityPanel from '../../../../assessment/components/FrameworkApplicabilityPanel'
import ThreatContextCards from '../../../../results/components/ThreatContextCards'
import { calculateRiskMaturityAlignment, getEnhancedGapAnalysis } from '../../../../../shared/utils/scoring'

const AI_KEYWORDS = ['artificial intelligence', 'machine learning', 'algorithmic', 'ai governance', 'ai accountability', 'generative ai', 'large language model', 'automated decision', 'ai transparency', 'ai safety', 'ai risk', 'model risk']

const SEVERITY_CONFIG = {
  high: { label: 'High', badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', border: 'border-l-red-500' },
  medium: { label: 'Medium', badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400', border: 'border-l-amber-500' },
  low: { label: 'Low', badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', border: 'border-l-blue-500' }
}

const CATEGORY_LABELS = {
  breach_notification: 'Breach Notification', penalty: 'Penalty Update', framework: 'Framework Update',
  authority: 'Authority Change', timeline: 'Timeline Change', new_regulation: 'New Regulation', ai_regulation: 'AI Regulation'
}

const STALENESS_THRESHOLD_DAYS = 90

function RiskIntelligenceTab({ client, defaultSubTab, defaultSection, breachResponseOnly = false }) {
  // Map combined view keys to their initial pill
  const initialSection = defaultSection
    || (defaultSubTab === 'threat-impact' ? 'threat'
    : defaultSubTab === 'regulatory-compliance' ? 'regulatory'
    : defaultSubTab || 'threat')
  const [section, setSection] = useState(initialSection)

  // Sync section when defaultSubTab or defaultSection changes
  useEffect(() => {
    if (defaultSection) {
      setSection(defaultSection)
      return
    }
    const newSection = defaultSubTab === 'threat-impact' ? 'threat'
      : defaultSubTab === 'regulatory-compliance' ? 'regulatory'
      : null
    if (newSection) setSection(newSection)
  }, [defaultSubTab, defaultSection])

  const [dismissedStaleWarning, setDismissedStaleWarning] = useState(false)
  const { saveResponse } = useAssessment()

  // Use most recent assessment (completed first, then in-progress)
  const allAssessments = (client.assessments || [])
  const completedAssessments = allAssessments
    .filter(a => a.status === 'completed')
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
  const activeAssessments = allAssessments.filter(a => a.status === 'in_progress')
  const latestAssessment = completedAssessments[0] || activeAssessments[0]

  // Local responses state mirrors the assessment's responses for the embedded components
  const [localResponses, setLocalResponses] = useState(latestAssessment?.responses || {})

  // Sync localResponses when assessment data changes (e.g., from industry cascade)
  useEffect(() => {
    if (latestAssessment?.responses) {
      setLocalResponses(latestAssessment.responses)
    }
  }, [latestAssessment?.responses])

  // Compute maturity score for impact calibration loss scenarios
  const maturityScore = (() => {
    if (latestAssessment?.scores?.overall?.score != null) {
      return latestAssessment.scores.overall.score
    }
    try {
      const scores = calculateScores(localResponses)
      return scores.overall?.score || 50
    } catch {
      return 50
    }
  })()

  // Check if threat data is stale (older than threshold)
  const staleInfo = useMemo(() => {
    if (!latestAssessment) return null
    const referenceDate = latestAssessment.completedAt || latestAssessment.updatedAt
    if (!referenceDate) return null
    const daysSince = Math.floor((Date.now() - new Date(referenceDate).getTime()) / (1000 * 60 * 60 * 24))
    if (daysSince < STALENESS_THRESHOLD_DAYS) return null
    return { daysSince, date: new Date(referenceDate).toLocaleDateString() }
  }, [latestAssessment])

  // Resolve selected threat profile for compliance insights
  const selectedProfile = useMemo(() => {
    const profileKey = localResponses?.threatContext?.profileKey
    if (!profileKey) return null
    return threatProfiles.profiles.find(p => p.verticalKey === profileKey) || null
  }, [localResponses?.threatContext?.profileKey])

  // Compute gap analysis for ThreatContextCards
  const gapAnalysis = useMemo(() => {
    if (!latestAssessment?.responses?.maturityAssessment) return null
    try {
      const scores = calculateScores(localResponses)
      const alignments = calculateRiskMaturityAlignment(scores.riskAppetite, scores.maturity)
      if (!alignments) return null
      const industry = localResponses?.businessScoping?.['bs-0']?.value || client?.industry || 'Other'
      return getEnhancedGapAnalysis(alignments, industry, localResponses?.businessScoping)
    } catch { return null }
  }, [localResponses, client?.industry, latestAssessment?.responses?.maturityAssessment])

  const handleSave = useCallback((key, data) => {
    if (!latestAssessment?.id) return
    const newResponses = { ...localResponses, [key]: data }
    setLocalResponses(newResponses)
    saveResponse(latestAssessment.id, key, null, data)
  }, [latestAssessment?.id, localResponses, saveResponse])

  if (!latestAssessment) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 mb-2">No risk intelligence data yet</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Start an assessment to generate threat context and impact calibration data.
          </p>
        </div>
      </div>
    )
  }

  const PAGE_TITLES = {
    threat: 'Threat Context',
    impact: 'Impact Calibration',
    regulatory: 'Regulatory Guidance',
    compliance: 'Regulatory Profile',
    updates: 'Regulatory Updates'
  }

  return (
    <div className="space-y-6">
      {/* Page title and source */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{PAGE_TITLES[section] || 'Risk Intelligence'}</h2>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Source: {latestAssessment.title || 'Assessment'} ({latestAssessment.status === 'completed' ? 'Completed' : 'In Progress'})
        </span>
      </div>

      {/* Staleness Warning */}
      {staleInfo && !dismissedStaleWarning && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Threat data may be outdated
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              Last updated {staleInfo.daysSince} days ago ({staleInfo.date}). The threat landscape evolves rapidly — consider reviewing threat actor weights, attack vectors, and impact calibration to ensure they reflect current conditions.
            </p>
          </div>
          <button
            onClick={() => setDismissedStaleWarning(true)}
            className="text-amber-400 hover:text-amber-600 dark:hover:text-amber-300 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {section === 'threat' && (
        <>
          <ThreatContextStep
            responses={localResponses}
            onSave={handleSave}
            client={client}
          />
          {gapAnalysis && (
            <ThreatContextCards
              assessment={latestAssessment}
              gapAnalysis={gapAnalysis}
              scores={null}
            />
          )}
        </>
      )}
      {section === 'impact' && (
        <ImpactCalibrationStep
          responses={localResponses}
          onSave={handleSave}
          maturityScore={maturityScore}
          client={client}
        />
      )}
      {section === 'regulatory' && (
        <RegulatoryContextSection
          responses={localResponses}
          client={client}
          breachResponseOnly={breachResponseOnly}
        />
      )}
      {section === 'compliance' && (
        <RegulatoryProfilePage
          client={client}
          responses={localResponses}
          selectedProfile={selectedProfile}
        />
      )}
      {section === 'updates' && (
        <ClientRegulatoryUpdates client={client} responses={localResponses} />
      )}
    </div>
  )
}

// ── Client-Scoped Regulatory Updates ─────────────────────────────────────────
// ── Regulatory Profile Page — Tabbed Premium Layout ─────────────────────────
function RegulatoryProfilePage({ client, responses, selectedProfile }) {
  const [activeTab, setActiveTab] = useState('context')

  const industry = client?.industry || 'your industry'
  const subIndustry = client?.industrySubCategory || client?.industry_sub_category || ''
  const industryLabel = subIndustry ? `${industry}: ${subIndustry}` : industry

  // Count applicable frameworks
  const baseFrameworks = selectedProfile?.regulatoryContext?.frameworks || []
  const overlayFrameworks = responses?.businessScoping?.['rf-1']?.value || []
  const totalFrameworks = new Set([...baseFrameworks, ...overlayFrameworks.map(f => f.toUpperCase())]).size

  // Max penalty
  const maxPenalty = selectedProfile?.regulatoryContext?.maxPenalty
  const maxPenaltyFormatted = maxPenalty ? `$${maxPenalty.toLocaleString()}` : 'N/A'

  const tabs = [
    { id: 'context', label: 'Regulatory Context' },
    { id: 'frameworks', label: 'Compliance Scope' },
    { id: 'recommendations', label: 'Next Steps' }
  ]

  return (
    <div className="space-y-0">
      {/* Executive overview paragraph */}
      <div className="mb-6">
        <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">
          Based on {client?.name ? <span className="font-medium text-gray-800 dark:text-gray-200">{client.name}</span> : "this organization"}'s
          {' '}<span className="font-medium text-gray-800 dark:text-gray-200">{industryLabel}</span> classification,
          {' '}<span className="font-medium text-gray-800 dark:text-gray-200">{totalFrameworks} regulatory framework{totalFrameworks !== 1 ? 's' : ''}</span> apply
          to your operations with penalty exposure up to <span className="font-medium text-red-600 dark:text-red-400">{maxPenaltyFormatted}</span>.
          {selectedProfile?.regulatoryContext?.breachNotificationDays && (
            <> Incidents require notification within <span className="font-medium text-gray-800 dark:text-gray-200">{selectedProfile.regulatoryContext.breachNotificationDays} days</span>.</>
          )}
          {' '}The following sections detail your regulatory obligations, compliance coverage, and recommended actions.
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200 dark:border-gray-700 mt-4">
        <nav className="flex gap-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="pt-6" />
      {activeTab === 'context' && (
        <RegulatoryContextSection
          responses={responses}
          client={client}
          hideBreachResponse
        />
      )}

      {activeTab === 'frameworks' && (
        <FrameworkApplicabilityPanel
          profile={selectedProfile}
          responses={responses}
          client={client}
        />
      )}

      {activeTab === 'recommendations' && (
        <div className="space-y-5">
          {/* AI Regulatory Exposure if applicable */}
          {responses?.businessScoping?.['bs-33'] && !responses.businessScoping['bs-33'].toString().toLowerCase().includes('not currently') && !responses.businessScoping['bs-33'].toString().toLowerCase().includes('no,') && (
            <div className="p-4 rounded-lg border border-violet-200 dark:border-violet-800/50 bg-violet-50 dark:bg-violet-900/10">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h4 className="text-sm font-semibold text-violet-800 dark:text-violet-300">AI Governance Consideration</h4>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                This organization uses AI tools. Ensure AI governance policies align with applicable framework requirements, particularly around data processing, model transparency, and third-party AI vendor oversight.
              </p>
            </div>
          )}

          {/* Recommended next steps */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Recommended Actions</h3>
            </div>
            <div className="space-y-3">
              {(() => {
                const recs = []
                const frameworks = baseFrameworks.map(f => f.toLowerCase())

                if (frameworks.some(f => f.includes('hipaa'))) {
                  recs.push({ priority: 'high', text: 'Conduct a formal HIPAA Security Rule risk assessment to validate full compliance posture' })
                }
                if (frameworks.some(f => f.includes('pci')) || overlayFrameworks.includes('pci-dss')) {
                  recs.push({ priority: 'high', text: 'Complete PCI DSS SAQ self-assessment for payment card infrastructure' })
                }
                if (frameworks.some(f => f.includes('glba'))) {
                  recs.push({ priority: 'medium', text: 'Conduct a focused GLBA Safeguards Rule review to evaluate compliance coverage' })
                }
                if (frameworks.some(f => f.includes('cis'))) {
                  recs.push({ priority: 'medium', text: 'Map CIS Controls Implementation Group 1 gaps to the enablement plan' })
                }
                recs.push({ priority: 'low', text: 'Schedule an annual regulatory review to reassess framework applicability as the organization evolves' })

                return recs.slice(0, 5).map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${
                      rec.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      : rec.priority === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    }`}>
                      {idx + 1}
                    </span>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{rec.text}</p>
                  </div>
                ))
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ClientRegulatoryUpdates({ client, responses }) {
  const [expandedId, setExpandedId] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [showArchive, setShowArchive] = useState(false)

  const clientIndustry = (client?.industry || '').toLowerCase()
  const clientSubIndustry = (client?.industry_sub_category || '').toLowerCase()

  const ROLLING_WINDOW_DAYS = regulatoryUpdates?.rollingWindowDays || 60
  const cutoffDate = new Date(Date.now() - ROLLING_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString()

  // Filter by client industry
  const filterByIndustry = useCallback((findings) => {
    return findings.filter(f => {
      if (!f.affectedIndustries || f.affectedIndustries.length === 0) return true
      return f.affectedIndustries.some(ind => {
        const lower = ind.toLowerCase()
        return clientIndustry.includes(lower) || lower.includes(clientIndustry) ||
               (clientSubIndustry && (lower.includes(clientSubIndustry) || clientSubIndustry.includes(lower)))
      })
    })
  }, [clientIndustry, clientSubIndustry])

  // Active findings (within rolling window, client-filtered)
  const clientFindings = useMemo(() => {
    const active = (regulatoryUpdates?.findings || []).filter(f => (f.publishedAt || f.detectedAt) >= cutoffDate)
    return filterByIndustry(active).sort((a, b) => {
      const sev = { high: 0, medium: 1, low: 2 }
      if (sev[a.severity] !== sev[b.severity]) return sev[a.severity] - sev[b.severity]
      return new Date(b.publishedAt || b.detectedAt) - new Date(a.publishedAt || a.detectedAt)
    })
  }, [cutoffDate, filterByIndustry])

  // Archived findings (outside rolling window, client-filtered)
  const archivedFindings = useMemo(() => {
    const archived = regulatoryUpdates?.archivedFindings || []
    return filterByIndustry(archived).sort((a, b) => new Date(b.publishedAt || b.detectedAt) - new Date(a.publishedAt || a.detectedAt))
  }, [filterByIndustry])

  const allFindings = showArchive ? archivedFindings : clientFindings

  // Unique categories from current view
  const categories = useMemo(() => [...new Set(allFindings.map(f => f.category))], [allFindings])

  // AI-specific findings from active feed
  const aiFeed = useMemo(() => {
    return clientFindings.filter(f => {
      const text = `${f.title} ${f.summary || ''} ${f.keyTakeaway || ''}`.toLowerCase()
      return f.category === 'ai_regulation' || AI_KEYWORDS.some(kw => text.includes(kw))
    })
  }, [clientFindings])

  // Filtered list
  const filtered = useMemo(() => {
    let results = allFindings
    if (severityFilter !== 'all') results = results.filter(f => f.severity === severityFilter)
    if (categoryFilter !== 'all') {
      if (categoryFilter === 'ai_governance') {
        results = results.filter(f => {
          const text = `${f.title} ${f.summary || ''} ${f.keyTakeaway || ''}`.toLowerCase()
          return f.category === 'ai_regulation' || AI_KEYWORDS.some(kw => text.includes(kw))
        })
      } else {
        results = results.filter(f => f.category === categoryFilter)
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      results = results.filter(f =>
        f.title.toLowerCase().includes(q) || (f.summary || '').toLowerCase().includes(q) || (f.keyTakeaway || '').toLowerCase().includes(q)
      )
    }
    return results
  }, [allFindings, severityFilter, categoryFilter, searchQuery])

  // AI Regulatory Exposure card data
  const aiUsage = responses?.businessScoping?.['bs-33']
  const aiProcurement = responses?.businessScoping?.['bs-34']
  const aiVendor = responses?.businessScoping?.['bs-35']
  const hasAiExposure = aiUsage && !aiUsage.toString().toLowerCase().includes('not currently in use') && !aiUsage.toString().toLowerCase().includes('no —')
  const isExtensive = aiUsage?.toString().toLowerCase().includes('extensive')
  const isModerate = aiUsage?.toString().toLowerCase().includes('moderate')
  const industry = client?.industry || ''

  const landscapeItems = useMemo(() => {
    if (!hasAiExposure) return []
    const items = []
    if (industry.toLowerCase().includes('health')) {
      items.push({ text: 'HHS guidance on AI in clinical decision support requires transparency and human oversight for patient safety', type: 'industry' })
    } else if (industry.toLowerCase().includes('financial') || industry.toLowerCase().includes('banking')) {
      items.push({ text: 'Federal banking regulators (OCC, FDIC, Fed) joint guidance on model risk management (SR 11-7) applies to AI/ML models', type: 'industry' })
      items.push({ text: 'SEC AI disclosure requirements for investment advisers using AI in portfolio management', type: 'industry' })
    } else if (industry.toLowerCase().includes('insurance')) {
      items.push({ text: 'NAIC Model Bulletin on AI governance requires risk management frameworks for AI in underwriting and claims', type: 'industry' })
    } else if (industry.toLowerCase().includes('education')) {
      items.push({ text: 'ED guidance on AI in educational settings emphasizes FERPA compliance for student data in AI-powered tools', type: 'industry' })
    } else if (industry.toLowerCase().includes('government')) {
      items.push({ text: 'Executive Order on Safe, Secure, and Trustworthy AI establishes federal agency requirements for AI risk management', type: 'industry' })
    }
    items.push({ text: 'NIST AI Risk Management Framework (AI RMF 1.0) — voluntary guidance for managing AI system risks across the lifecycle', type: 'framework' })
    if (isExtensive || isModerate) items.push({ text: 'ISO/IEC 42001 (AI Management System) — requirements for organizations deploying AI at scale', type: 'framework' })
    if (isExtensive) items.push({ text: 'EU AI Act risk classification framework may apply to AI systems serving EU customers or processing EU resident data', type: 'framework' })
    if (aiProcurement?.toString().toLowerCase().includes('ad hoc') || aiProcurement?.toString().toLowerCase().includes('unmanaged'))
      items.push({ text: 'Shadow AI risk: unmanaged AI procurement increases exposure to data leakage and compliance violations', type: 'warning' })
    if (aiVendor?.toString().toLowerCase().includes('no awareness') || aiVendor?.toString().toLowerCase().includes('no governance'))
      items.push({ text: 'Third-party AI governance gap: vendor AI usage without oversight may create compliance exposure', type: 'warning' })
    return items
  }, [hasAiExposure, industry, isExtensive, isModerate, aiProcurement, aiVendor])

  const lastChecked = regulatoryUpdates?.lastChecked ? new Date(regulatoryUpdates.lastChecked).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null

  return (
    <div className="space-y-6">
      {/* AI Regulatory Exposure Card */}
      {hasAiExposure && (
        <div className="p-4 rounded-lg border border-violet-200 dark:border-violet-800/50 bg-violet-50 dark:bg-violet-900/10">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mt-0.5">
              <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-violet-800 dark:text-violet-300">AI Regulatory Exposure</h4>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-200 dark:bg-violet-800/40 text-violet-700 dark:text-violet-300 font-medium">
                    {isExtensive ? 'High' : isModerate ? 'Moderate' : 'Low'}
                  </span>
                </div>
                {lastChecked && <span className="text-[10px] text-gray-400 dark:text-gray-500">Updated {lastChecked}</span>}
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Your organization has {isExtensive ? 'extensive' : isModerate ? 'moderate' : 'limited'} AI adoption.
                Regulatory landscape and recent activity relevant to your environment:
              </p>

              <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Regulatory Landscape</p>
              <ul className="space-y-1.5 mb-4">
                {landscapeItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                    <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 ${item.type === 'warning' ? 'bg-amber-400' : item.type === 'industry' ? 'bg-violet-400' : 'bg-gray-400 dark:bg-gray-500'}`} />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>

              <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Recent AI Activity</p>
              {aiFeed.length > 0 ? (
                <div className="space-y-2">
                  {aiFeed.slice(0, 3).map(f => {
                    const sevColor = f.severity === 'high' ? 'bg-red-500' : f.severity === 'medium' ? 'bg-amber-500' : 'bg-blue-500'
                    return (
                      <div key={f.id} className="flex items-start gap-2 text-xs">
                        <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 ${sevColor}`} />
                        <div className="min-w-0">
                          <span className="text-gray-800 dark:text-gray-200 font-medium">{f.title}</span>
                          {f.publishedAt && <span className="text-gray-400 dark:text-gray-500 ml-1.5">{new Date(f.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic">No new AI regulatory activity in the current monitoring window.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header + Active/Archive toggle */}
      <div className="card p-4">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-3">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Regulatory Updates</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Filtered for {client?.industry || 'this client'}{client?.industry_sub_category ? `: ${client.industry_sub_category}` : ''}
              {lastChecked && <> &middot; Last checked {lastChecked}</>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
              <button
                onClick={() => setShowArchive(false)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${!showArchive ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
              >
                Active ({clientFindings.length})
              </button>
              <button
                onClick={() => setShowArchive(true)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${showArchive ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
              >
                Archive ({archivedFindings.length})
              </button>
            </div>
            <Link
              to="/regulatory-updates"
              className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              View all
            </Link>
          </div>
        </div>

        {/* Filters row */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search updates..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={severityFilter}
            onChange={e => setSeverityFilter(e.target.value)}
            className="pl-3 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Severities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="pl-3 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Categories</option>
            <option value="ai_governance">AI Governance</option>
            {categories.map(c => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Findings count */}
      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
        <span>{filtered.length} {showArchive ? 'archived' : 'active'} update{filtered.length !== 1 ? 's' : ''} for {client?.name || 'this client'}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> {filtered.filter(f => f.severity === 'high').length} high</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> {filtered.filter(f => f.severity === 'medium').length} medium</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> {filtered.filter(f => f.severity === 'low').length} low</span>
      </div>

      {/* Findings list */}
      {filtered.length === 0 ? (
        <div className="card p-8 text-center">
          <svg className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">No regulatory updates match your client's profile in the current window.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(finding => {
            const config = SEVERITY_CONFIG[finding.severity] || SEVERITY_CONFIG.low
            const isExpanded = expandedId === finding.id
            return (
              <div
                key={finding.id}
                id={`finding-${finding.id}`}
                className={`card border-l-4 ${config.border} overflow-hidden transition-all`}
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : finding.id)}
                  className="w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${config.badge}`}>{config.label}</span>
                        {finding.category && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            {CATEGORY_LABELS[finding.category] || finding.category}
                          </span>
                        )}
                        {(finding.affectedIndustries || []).slice(0, 3).map(ind => (
                          <span key={ind} className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">{ind}</span>
                        ))}
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">{finding.title}</h4>
                      {!isExpanded && finding.summary && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{finding.summary}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        {finding.publishedAt ? new Date(finding.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </span>
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                    {finding.summary && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Detail</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{finding.summary}</p>
                      </div>
                    )}
                    {finding.keyTakeaway && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Recommendation</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{finding.keyTakeaway}</p>
                      </div>
                    )}
                    {finding.platformAction && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Platform Action</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{finding.platformAction}</p>
                      </div>
                    )}
                    {finding.sourceUrl && (
                      <a href={finding.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline">
                        View source
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default RiskIntelligenceTab
